import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { FIGURE_LABELS, FIGURE_SETS, POINT, REGION_NAMES, detectRegions, figureSrc, getFigures, setFigures } from "@/lib/bodyRegions";

// Container-space points: solo figure fills the frame; a pair sits side by side (energy rises in one, crosses, returns through the other).
const route = (regions, n) => {
  const pts = (fig, list) => list.map((r) => ({ r, fig, x: (fig * 100 + POINT[r][0]) / n, y: POINT[r][1] }));
  return n === 1 ? pts(0, regions) : [...pts(0, regions), ...pts(1, [...regions].reverse())];
};

const Chips = ({ value, onChange, testId }) => (
  <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Figures">
    {Object.keys(FIGURE_SETS).map((k) => (
      <button key={k} type="button" role="radio" aria-checked={value === k} data-testid={`${testId}-fig-${k}`} onClick={() => onChange(k)}
        className={`rounded-full border px-3 py-1.5 text-[12px] transition-colors ${value === k ? "bg-[#D9B47A] border-[#D9B47A] text-[#0A0706]" : "border-[#D9B47A]/25 text-[#CFC2B6] hover:border-[#D9B47A]/60"}`}>{FIGURE_LABELS[k]}</button>
    ))}
  </div>
);

// Shows where a practice moves energy through the body, on one figure or a pair. Renders nothing if no body area is named.
export default function EnergyMap({ text, partner = false, testId = "energy-map" }) {
  const regions = useMemo(() => detectRegions(text), [text]);
  const [set, setSet] = useState(() => getFigures()[partner ? "pair" : "solo"]);
  if (!regions.length) return null;
  const figs = FIGURE_SETS[set] ?? FIGURE_SETS.female;
  const n = figs.length;
  const pts = route(regions, n);
  const choose = (k) => {
    setSet(k);
    setFigures({ [FIGURE_SETS[k].length === 2 ? "pair" : "solo"]: k });
  };
  const dur = Math.max(2.4, pts.length * 1.1);
  return (
    <figure data-testid={testId} data-regions={regions.join(",")} className="mt-6 border border-[#D9B47A]/15 bg-[#0A0706] p-4">
      <Chips value={set} onChange={choose} testId={testId} />
      <div className="relative mx-auto mt-4 w-full overflow-hidden" style={{ maxWidth: n === 1 ? 240 : 420, aspectRatio: `${848 * n} / 1264` }}>
        <div className="absolute inset-0 flex">
          {figs.map((f, i) => <img key={`${f}-${i}`} src={figureSrc(f)} alt={`${f === "male" ? "Male" : "Female"} figure`} draggable={false} className="h-full object-cover select-none" style={{ width: `${100 / n}%` }} />)}
        </div>
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden>
          {pts.length > 1 && <polyline points={pts.map((p) => `${p.x},${p.y}`).join(" ")} fill="none" stroke="#F4D9A4" strokeOpacity="0.45" strokeWidth="1.5" strokeDasharray="3 4" vectorEffect="non-scaling-stroke" />}
        </svg>
        {pts.map((p, i) => (
          <span key={`${p.r}-${p.fig}-${i}`} data-testid={`${testId}-point-${p.r}${p.fig ? "-b" : ""}`} aria-hidden className="absolute w-3 h-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#E8838E] shadow-[0_0_14px_rgba(232,131,142,0.9)] animate-pulse" style={{ left: `${p.x}%`, top: `${p.y}%` }} />
        ))}
        {pts.length > 0 && (
          <motion.span aria-hidden className="absolute w-5 h-5 -translate-x-1/2 -translate-y-1/2 rounded-full pointer-events-none"
            style={{ background: "radial-gradient(circle, #FFF3D6 0%, rgba(244,217,164,0.8) 35%, transparent 70%)", left: `${pts[0].x}%`, top: `${pts[0].y}%` }}
            animate={pts.length > 1 ? { left: pts.map((p) => `${p.x}%`), top: pts.map((p) => `${p.y}%`) } : { scale: [0.8, 1.5, 0.8] }}
            transition={{ duration: dur, repeat: Infinity, ease: "easeInOut" }} />
        )}
      </div>
      <figcaption data-testid={`${testId}-caption`} className="mt-3 text-center text-[13px] text-[#CFC2B6]">
        {regions.map((r) => REGION_NAMES[r]).join(" → ")}{n === 2 ? " · then through your partner" : ""}
      </figcaption>
    </figure>
  );
}
